import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FieldComponent, InputField } from '@jutro/components';
import styles from './AutoCompleteInput.module.scss';
import AutoTypeDropdownComponent from './AutoTypeDropdownComponent';

/**
 * Searches the items for the query.
 * If *searchByProperty* is passed, only such property is searched in the items
 *
 * @param {Array} items items to search
 * @param {string} query the query entered by the user
 * @param {string} searchByProperty a property name available in the elements passed in *items*
 * @returns {Array} an array with items filtered by the user query
 *
 */
function searchItems(items, query, searchByProperty) {
    const { nextIndex, currentIndex } = this.state;

    if (_.isNil(query)) {
        return [];
    }

    items.sort((a, b) => {
        return (a.addressBookUID) - (b.addressBookUID);
    });
    const filterItems = [];
    _.forEach(items,
        (item, index) => {
            if (item.addressBookUID !== 'Other' && index >= currentIndex && index < nextIndex) {
                filterItems.push(item);
            }
        });
    filterItems.push({ addressBookUID: 'Other', companyName: 'Other', companyAddress: '' });


    const filteredItems = filterItems.filter((item) => {
        if (searchByProperty) {
            return _.get(item, searchByProperty, '').toLowerCase().includes(query.toLowerCase());
        }
        return ((item || '').includes(query.toLowerCase()) || (item).includes('other'));
    });

    return filteredItems;
}

class AutoCompleteInput extends FieldComponent {
    /**
     * @static
     * @memberof AutoCompleteInput
     * @mixes InputField.propTypes
     * @prop {Array} items the items to be searched by the user.
     *                      The array can contain strings or objects
     * @prop {function(userInput) => (Array|Promise<Array>)} onSearchItems
     *                      a function used to search the items based on user input
     *                      The function can return an array or a promise that
     *                      will resolve to an array
     * @prop {string} renderAs a path to a property in *item* that is used to
     *                                      render the result or a function that, given an item,
     *                                      returns a string
     * @prop {function(item): string} onRenderItem a function used to get the string to be used
     *                                      when rendering an item. If **renderAs** is provided
     *                                      this is **ignored**
     * @prop {string} searchBy **only** if *items* is provided as an Array
     *                          this represents the property used when searching for user input
     * @prop {number} throttleMs number of milliseconds to wait before
     *                           re-evaluating a user input change
     * @prop {number} maxResults  maximum number of results to show in the dropdown
     */
    static propTypes = {
        ...InputField.propTypes,
        items: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})])),
        onSearchItems: PropTypes.func,
        data: PropTypes.string,
        onClearTypeaheadInput: PropTypes.func,
        renderAs: PropTypes.string,
        onRenderItem: PropTypes.func,
        onRenderValueItem: PropTypes.func,
        searchBy: PropTypes.string,
        throttleMs: PropTypes.number,
        maxResults: PropTypes.number,
        id: PropTypes.string.isRequired
    };

    static defaultProps = {
        ...InputField.defaultProps,
        items: [],
        onSearchItems: undefined,
        onClearTypeaheadInput: undefined,
        renderAs: undefined,
        onRenderItem: undefined,
        onRenderValueItem: undefined,
        throttleMs: 100,
        data: '',
        maxResults: undefined
    };

    constructor(props, context) {
        super(props, context);
        const { onSearchItems, throttleMs, data } = props;

        this.state = {
            rawInput: data,
            resolvedItems: [],
            maxResult: 10
        };

        this.dropdownRef = null;

        if (_.isFunction(onSearchItems)) {
            this.searchItems = _.throttle(onSearchItems, throttleMs);
        }
    }

    handleCancelSelection = () => {
        this.setState({
            resolvedItems: []
        });
    };

    handleRawInputChange = (value) => {
        const { onValueChange, items, searchBy, path } = this.props;
        const { rawInput } = this.state;

        // reset potentially resolved items
        this.setState({
            rawInput: value,
            resolvedItems: []
        });

        // case items is array
        if (_.isArray(items) && !_.isEmpty(items)) {
            this.setState({
                resolvedItems: searchItems(items, value, searchBy)
            });
        }

        // case we need to search with a function
        if (_.isFunction(this.searchItems)) {
            /*
                wrap the function in a promise, so:
                - if the function returns a value, we'll be able to treat it as a promise
                - if the function returns a promise, we'll transparently use the resulting promise
            */
            Promise.resolve(this.searchItems(value)).then((resolvedItems) => {
                this.setState({
                    resolvedItems: resolvedItems
                });
            });
        }
        if (onValueChange) {
            this.setState({
                currentIndex: 0,
                nextIndex: 4
            });
            if (rawInput === '') {
                onValueChange(undefined, path);
            }
        }
    };

    handleUserSelects = (item) => {
        const {
            onValueChange, path, renderAs, onRenderItem
        } = this.props;

        let renderableItem;

        if (!_.isNil(renderAs)) {
            renderableItem = _.get(item, renderAs);
        } else if (!_.isNil(onRenderItem)) {
            renderableItem = onRenderItem(item);
        } else {
            renderableItem = item;
        }

        this.setState({
            rawInput: item !== undefined ? _.get(item, 'companyName') : '',
            resolvedItems: []
        });
        if (onValueChange) {
            this.setState({
                currentIndex: 0,
                nextIndex: 4
            });
            onValueChange(item, path);
        }
    };

    handleOnFocus = () => {
        const { rawInput } = this.state;
        if (_.isEmpty(rawInput)) {
            // simulate a search for an empty string
            this.handleRawInputChange('');
        }
    };

    handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            this.handleCancelSelection();
        } else if (event.key === 'ArrowDown') {
            if (this.dropdownRef) {
                event.preventDefault();
                this.dropdownRef.focus();
            }
        }
    };

    setupDropdownRef = (domRef) => {
        this.dropdownRef = domRef;
    };

    getRawInputValue = () => {
        const { value, renderAs, onRenderValueItem } = this.props;
        const { rawInput } = this.state;
        let valueForRawInput;
        if (_.isNil(rawInput)) {
            if (!_.isEmpty(renderAs) && _.isString(renderAs)) {
                valueForRawInput = _.get(value, renderAs, '');
            } else {
                valueForRawInput = value;
            }
        } else if (!_.isEmpty(value) && !(value.length > rawInput.length)) {
            valueForRawInput = _.isFunction(onRenderValueItem) ? onRenderValueItem(value) : value;
        } else {
            valueForRawInput = rawInput;
        }
        return valueForRawInput;
    };

    render() {
        return super.render();
    }

    setInputEvents = (element) => {
        if (element) {
            const input = element.querySelector('input');
            input.addEventListener('keydown', this.handleKeyDown);
        }
    };

    clearSelectionAndInput = () => {
        const { onClearTypeaheadInput, path } = this.props;

        this.setState({
            rawInput: '',
            resolvedItems: []
        });
        if (onClearTypeaheadInput) {
            onClearTypeaheadInput(undefined, path);
        }
    };

    renderNext = (id) => {
        const { maxResult, resolvedItems } = this.state;
        if (resolvedItems.length > maxResult) {
            this.setState({ maxResult: maxResult + 10 });
        }
        const scroll = document.getElementById(id);
        scroll.scrollTop = scroll.scrollHeight - scroll.clientHeight;
    };

    renderItem = (contact) => {
        const { rawInput } = this.state;
        const highlighted = contact.companyName.substring(0, rawInput.length);
        const secondPart = contact.companyName.substring(rawInput.length);
        return (
            <div>
                <b className={styles.highlight}>{highlighted}</b>
                <span>{secondPart}</span>
                <br />
                <span>{contact.companyAddress}</span>
            </div>
        );
    };

    renderControl() {
        const { resolvedItems, maxResult } = this.state;
        const {
            renderAs, id
        } = this.props;
        const inputProps = {
            ...this.props,
            hideLabel: true,
            onValueChange: this.handleRawInputChange,
            onKeyDown: this.handleKeyDown,
            onFocus: this.handleOnFocus,
            layout: 'full-width',
            value: this.getRawInputValue(),
            containerClass: styles.zeroPadding,
            className: styles.inputField,
            autoComplete: false
        };

        return (
            <div ref={this.setInputEvents} className={styles.gwTypeahead}>
                <InputField {...inputProps} />
                <AutoTypeDropdownComponent
                    id={id}
                    setupRef={this.setupDropdownRef}
                    items={resolvedItems}
                    onItemSelected={this.handleUserSelects}
                    onCancelSelection={this.handleCancelSelection}
                    renderAs={renderAs}
                    maxResults={maxResult}
                    onRenderItem={this.renderItem}
                    renderNext={this.renderNext}
                />
            </div>
        );
    }
}

export default AutoCompleteInput;
